import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EconomicsPatientNode } from '../../types/economicsTypes';
import { EconomicsCheckbox } from './EconomicsCheckbox';
import { EconomicsGroupRow } from './EconomicsGroupRow';
import { formatDkk } from '../../utils/currencyUtils';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import styles from './Economics.module.css';

interface EconomicsPatientRowProps {
  node: EconomicsPatientNode;
  canWrite: boolean;
  onToggleKeys: (keys: string[], completed: boolean) => void;
  onToggleLeaf: (key: string, completed: boolean) => void;
  onOverrideChange: (key: string, value: number | null, note: string | null) => void;
}

export const EconomicsPatientRow = ({
  node,
  canWrite,
  onToggleKeys,
  onToggleLeaf,
  onOverrideChange
}: EconomicsPatientRowProps) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const keys = node.groups.map((g) => g.key);

  return (
    <>
      <div className={`${styles.row} ${styles.rowPatient}`}>
        <button
          type="button"
          className={styles.expandButton}
          onClick={() => setIsOpen((open) => !open)}
          aria-label={t('economics-toggle-expand')}
          aria-expanded={isOpen}
        >
          <ChevronIcon className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
        </button>
        <EconomicsCheckbox
          state={node.selectionState}
          onChange={(next) => onToggleKeys(keys, next)}
          disabled={!canWrite}
          ariaLabel={t('economics-complete-patient', { patient: node.patientName })}
        />
        <div className={styles.label}>
          <span>{node.patientName}</span>
          <span className={styles.count}>
            ({node.completedCount}/{node.groups.length})
          </span>
        </div>
        <div className={styles.spread}>
          <span className={styles.subtotal}>{formatDkk(node.total, i18n.language)}</span>
        </div>
      </div>
      {isOpen &&
        node.groups.map((group) => (
          <EconomicsGroupRow
            key={group.key}
            node={group}
            canWrite={canWrite}
            onToggle={onToggleLeaf}
            onOverrideChange={onOverrideChange}
          />
        ))}
    </>
  );
};
